import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from 'styled-system'
import { Flex, IconButton, Link } from 'pcln-design-system'
import {
  Phone as PhoneIcon,
  Close as CloseIcon,
  Menu as MenuIcon
} from 'pcln-icons'
import Account from '../Account'
import NavLogo from '../NavLogo'
import alignmentToJustify from '../../utils/alignmentToJustify'
import { alignmentTypes } from '../../utils/shapes'

const Container = styled(Flex)`
  position: relative;
  justify-content: ${props => props.justifyContent};
  align-items: center;
  height: ${props => props.height}px;
  padding: ${themeGet('space.2')}px ${themeGet('space.3')}px;

  background-color: ${props => props.backgroundColor};
`
Container.displayName = 'Container'

const LogoContainer = styled(Flex)`
  position: relative;
  flex: 1;
  justify-content: ${props => props.justifyContent};
  width: ${props =>
    props.justifyContent === 'space-between' ? '100%' : 'auto'};
  height: 100%;
`

const SharedButtonProps = `
  :focus {
    outline: none;
  }

  & svg {
    :focus {
      outline: none;
    }
  }
  
  -webkit-tap-highlight-color: rgba(0,0,0,0);
`

const RelativeMenuButton = styled(IconButton)`
  position: relative;
  display: flex;

  ${SharedButtonProps};
`
RelativeMenuButton.displayName = 'RelativeMenuButton'

const PhoneNumberLink = styled(Link)`
  min-width: fit-content;
  white-space: nowrap;
`

PhoneNumberLink.displayName = 'PhoneNumberLink'

function NavHeader ({
  isOpen,
  logoPath,
  logoHref,
  logoTitle,
  secondaryLogoPath,
  secondaryLogoHref,
  secondaryLogoTitle,
  phoneNumber,
  logoAlignment,
  isMenuButtonRightAligned,
  target,
  height,
  color,
  backgroundColor,
  onClick,
  hasNavigation
}) {
  const iconName = isOpen ? 'close' : 'menu'
  const justifyContent = secondaryLogoPath
    ? 'space-between'
    : alignmentToJustify(logoAlignment)

  return (
    <Container
      justifyContent={justifyContent}
      height={height}
      color={color}
      data-testid='nav-header'
      backgroundColor={backgroundColor}
    >
      {!isMenuButtonRightAligned &&
        hasNavigation && (
        <RelativeMenuButton
          icon={
            iconName === 'close' ? (
              <CloseIcon size={24} title='close' />
            ) : (
              <MenuIcon size={24} title='menu' />
            )
          }
          mr={16}
          onClick={onClick}
        />
      )}
      <LogoContainer justifyContent={justifyContent}>
        <NavLogo path={logoPath} href={logoHref} target={target} />
        {secondaryLogoPath && (
          <NavLogo
            path={secondaryLogoPath}
            href={secondaryLogoHref}
            target={target}
          />
        )}
      </LogoContainer>
      {phoneNumber && (
        <>
          <PhoneIcon pr={1} />
          <PhoneNumberLink href={`tel:${phoneNumber}`} color='text.dark'>
            {phoneNumber}
          </PhoneNumberLink>
        </>
      )}
      <Account />
      {isMenuButtonRightAligned &&
        hasNavigation && (
        <RelativeMenuButton
          icon={
            iconName === 'close' ? (
              <CloseIcon size={24} title='close' />
            ) : (
              <MenuIcon size={24} title='menu' />
            )
          }
          onClick={onClick}
        />
      )}
    </Container>
  )
}

NavHeader.displayName = 'NavHeader'

NavHeader.propTypes = {
  isOpen: PropTypes.bool,
  logoPath: PropTypes.string,
  logoHref: PropTypes.string,
  logoTitle: PropTypes.string,
  secondaryLogoPath: PropTypes.string,
  secondaryLogoHref: PropTypes.string,
  secondaryLogoTitle: PropTypes.string,
  phoneNumber: PropTypes.string,
  logoAlignment: alignmentTypes,
  isMenuButtonRightAligned: PropTypes.bool,
  target: PropTypes.string,
  height: PropTypes.number,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  hasNavigation: PropTypes.bool
}

export default memo(NavHeader)
